import React from 'react'
import { ArrowUp, ArrowDown, RotateCcw } from 'lucide-react'
import { FIELD_KEYS, getFieldValue } from '../../config/columns'
import { getStrikeStep } from '../../lib/analysis-utils'
import { formatOI, formatVolume, formatCombinedOI, formatCombinedVol } from '../../lib/data-utils'
import { GROUP_HEADER, getStrikeLabel, HEADER_CLASSES } from '../../lib/table-utils'
import type { AnalysisResult, MarketData, OptionData } from '../../types'
import { DataBar, NumberCell } from '../ui/ghost-table/DataCells'
import { LiveGlassCell } from '../ui/ghost-table/LiveGlassCell'
import { DataChip } from '../ui/ghost-table/DataChip'
import { GhostTh, OptionChainHeader } from '../ui/ghost-table/StickyHeader'
import { StrikeChip } from '../ui/ghost-table/StrikeChip'
// Ghost System Imports
import { TableWrapper } from '../ui/ghost-table/TableWrapper'
import { WidgetErrorBoundary } from '../ui/WidgetErrorBoundary'
import { useStrikePremiumProfile } from '../../hooks/useStrikePremiumProfile'
import { useSettingsStore } from '../../stores/settingsStore'

interface OptionChainTableProps
{
    marketData: MarketData | null
    analysis: AnalysisResult | null
    filteredOptions: OptionData[]
    baseOptions?: OptionData[]
    maxValues: {
        maxOI: number
        maxVol: number
        ceOIChange: number
        peOIChange: number
        ceChangeP: number
        peChangeP: number
        maxIV: number
        maxCombinedOI: number
        maxCombinedVol: number
        maxCombinedOIChange?: number
        maxCombinedVolChange?: number
        maxCombinedOIChangeP?: number
        maxCombinedVolChangeP?: number
    }
    isVisible: ( key: string ) => boolean
    className?: string
    style?: React.CSSProperties
}

const StrikeRow = React.memo(({ 
    row, rowIndex, isAtm, atmClass, hoverClass, isVisible, maxValues, 
    renderDataCell, renderGlassCell, top3OI, bot3OI, top3OIChg, bot3OIChg, 
    top3ChgOIP, bot3ChgOIP, top3Vol, bot3Vol, top3ChgVol, bot3ChgVol, 
    top3ChgVolP, bot3ChgVolP, analysis,
    top3VolDiff, bot3VolDiff, top3OIDiff, bot3OIDiff, baseOptions, symbol,
    premiumProfile, activeSubTab
}: any) => {
    const tdBase = 'p-2 h-[56px] align-middle whitespace-nowrap border-b border-slate-100/30 dark:border-slate-800/30 first:rounded-l-[12px] last:rounded-r-[12px]'
    const callBg = isAtm ? 'bg-indigo-500/10' : 'bg-rose-50/20 dark:bg-rose-950/5'
    const putBg = isAtm ? 'bg-indigo-500/10' : 'bg-emerald-50/20 dark:bg-emerald-950/5'

    const ceLtp = getFieldValue(row, FIELD_KEYS.CE_LTP) || 0
    const ceOI = getFieldValue(row, FIELD_KEYS.CE_OI) || 0
    const ceOIChg = getFieldValue(row, FIELD_KEYS.CE_OI_CHG) || 0
    const ceVol = getFieldValue(row, FIELD_KEYS.CE_VOL) || 0
    const ceIV = getFieldValue(row, FIELD_KEYS.CE_IV)
    const ceChgP = getFieldValue(row, FIELD_KEYS.CE_PRICE_CHG) || 0

    const peLtp = getFieldValue(row, FIELD_KEYS.PE_LTP) || 0
    const peOI = getFieldValue(row, FIELD_KEYS.PE_OI) || 0
    const peOIChg = getFieldValue(row, FIELD_KEYS.PE_OI_CHG) || 0
    const peVol = getFieldValue(row, FIELD_KEYS.PE_VOL) || 0
    const peIV = getFieldValue(row, FIELD_KEYS.PE_IV)
    const peChgP = getFieldValue(row, FIELD_KEYS.PE_PRICE_CHG) || 0

    const ceSuppVal = premiumProfile?.ceSupport ?? ceLtp
    const ceResVal = premiumProfile?.ceRes ?? ceLtp
    const peSuppVal = premiumProfile?.peSupport ?? peLtp
    const peResVal = premiumProfile?.peRes ?? peLtp

    // Vol-OI and Vol-OI-Chng calculations
    // CE side: Vol - OI
    const ceVolOi = ceVol - ceOI
    const ceVolOiChng = ceOIChg > 0 ? (ceVolOi - ceOIChg) : (ceVolOi + ceOIChg)

    // PE side: Vol - OI
    const peVolOi = peVol - peOI
    const peVolOiChng = peOIChg > 0 ? (peVolOi - peOIChg) : (peVolOi + peOIChg)

    const combinedOI = ceOI + peOI
    const combinedOIChange = ceOIChg + peOIChg
    const chgOIP = ceOI + peOI > 0 ? (combinedOIChange / (combinedOI - combinedOIChange)) * 100 : 0
    const combinedVol = ceVol + peVol
    const bOpt = baseOptions?.find( ( b: any ) => Number(b.strike) === Number(row.strike) )
    const bCombinedOI = bOpt ? ( (getFieldValue(bOpt, FIELD_KEYS.CE_OI) || 0) + (getFieldValue(bOpt, FIELD_KEYS.PE_OI) || 0) ) : 0
    const bCombinedVol = bOpt ? ( (getFieldValue(bOpt, FIELD_KEYS.CE_VOL) || 0) + (getFieldValue(bOpt, FIELD_KEYS.PE_VOL) || 0) ) : 0
    const hasBase = baseOptions && baseOptions.length > 0
    
    // If no base options exist yet (e.g., initial load before history fetch), we cannot calculate volume change
    const combinedVolChange = (hasBase && bOpt) ? combinedVol - bCombinedVol : null
    const chgVolP = (hasBase && bOpt && bCombinedVol !== 0) ? (combinedVolChange! / bCombinedVol) * 100 : null

    // Badges
    const oiRank = top3OI.indexOf(combinedOI)
    const oiBadge = oiRank !== -1 ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm ${oiRank === 0 ? 'bg-amber-400 text-amber-900 ring-1 ring-amber-500/30' : 'bg-amber-100 text-amber-700 ring-1 ring-amber-200/30'}`}>O{oiRank + 1}</span> : null
    
    const volRank = top3Vol.indexOf(combinedVol)
    const volBadge = volRank !== -1 ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm ${volRank === 0 ? 'bg-amber-400 text-amber-900 ring-1 ring-amber-500/30' : 'bg-amber-100 text-amber-700 ring-1 ring-amber-200/30'}`}>V{volRank + 1}</span> : null

    const volDiff = ceVol - peVol
    const oiDiff = peOI - ceOI
    
    const volDiffTopRank = top3VolDiff.indexOf(volDiff)
    const volDiffBotRank = bot3VolDiff.indexOf(volDiff)
    const volDiffBadge = volDiffTopRank !== -1 
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/30`}>T{volDiffTopRank + 1}</span> 
        : volDiffBotRank !== -1
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm bg-rose-100 text-rose-700 ring-1 ring-rose-200/30`}>B{volDiffBotRank + 1}</span>
        : null

    const oiDiffTopRank = top3OIDiff.indexOf(oiDiff)
    const oiDiffBotRank = bot3OIDiff.indexOf(oiDiff)
    const oiDiffBadge = oiDiffTopRank !== -1 
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/30`}>T{oiDiffTopRank + 1}</span> 
        : oiDiffBotRank !== -1
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm bg-rose-100 text-rose-700 ring-1 ring-rose-200/30`}>B{oiDiffBotRank + 1}</span>
        : null

    const oiChgTopRank = top3OIChg.indexOf(combinedOIChange)
    const oiChgBotRank = bot3OIChg.indexOf(combinedOIChange)
    const oiChgBadge = oiChgTopRank !== -1 
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm ${oiChgTopRank === 0 ? 'bg-amber-400 text-amber-900 ring-1 ring-amber-500/30' : 'bg-amber-100 text-amber-700 ring-1 ring-amber-200/30'}`}>T{oiChgTopRank + 1}</span> 
        : oiChgBotRank !== -1
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm bg-rose-100 text-rose-700 ring-1 ring-rose-200/30`}>B{oiChgBotRank + 1}</span>
        : null

    const oiChgPTopRank = top3ChgOIP.indexOf(chgOIP)
    const oiChgPBotRank = bot3ChgOIP.indexOf(chgOIP)
    const oiChgPBadge = oiChgPTopRank !== -1 
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm ${oiChgPTopRank === 0 ? 'bg-amber-400 text-amber-900 ring-1 ring-amber-500/30' : 'bg-amber-100 text-amber-700 ring-1 ring-amber-200/30'}`}>T{oiChgPTopRank + 1}</span> 
        : oiChgPBotRank !== -1
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm bg-rose-100 text-rose-700 ring-1 ring-rose-200/30`}>B{oiChgPBotRank + 1}</span>
        : null

    const volChgTopRank = top3ChgVol.indexOf(combinedVolChange)
    const volChgBotRank = bot3ChgVol.indexOf(combinedVolChange)
    const volChgBadge = volChgTopRank !== -1 
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm ${volChgTopRank === 0 ? 'bg-amber-400 text-amber-900 ring-1 ring-amber-500/30' : 'bg-amber-100 text-amber-700 ring-1 ring-amber-200/30'}`}>T{volChgTopRank + 1}</span> 
        : volChgBotRank !== -1
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm bg-rose-100 text-rose-700 ring-1 ring-rose-200/30`}>B{volChgBotRank + 1}</span>
        : null

    const volChgPTopRank = top3ChgVolP.indexOf(chgVolP)
    const volChgPBotRank = bot3ChgVolP.indexOf(chgVolP)
    const volChgPBadge = volChgPTopRank !== -1 
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm ${volChgPTopRank === 0 ? 'bg-amber-400 text-amber-900 ring-1 ring-amber-500/30' : 'bg-amber-100 text-amber-700 ring-1 ring-amber-200/30'}`}>T{volChgPTopRank + 1}</span> 
        : volChgPBotRank !== -1
        ? <span className={`inline-flex items-center justify-center h-4 px-1 rounded-md text-[8px] font-black leading-none shadow-sm bg-rose-100 text-rose-700 ring-1 ring-rose-200/30`}>B{volChgPBotRank + 1}</span>
        : null

    const rowLabel = getStrikeLabel(row.strike, analysis)

    return (
        <tr key={row.strike} className={`${atmClass} group ${hoverClass}`}>
            {/* CALLS SIDE */}
            {isVisible('delta') && <td className={`${tdBase} ${callBg} text-right`}><NumberCell value={getFieldValue(row, FIELD_KEYS.CE_DELTA) || 0} decimals={2} /></td>}
            {isVisible('gamma') && <td className={`${tdBase} ${callBg} text-right`}><NumberCell value={getFieldValue(row, FIELD_KEYS.CE_GAMMA) || 0} decimals={4} /></td>}
            {isVisible('theta') && <td className={`${tdBase} ${callBg} text-right`}><NumberCell value={getFieldValue(row, FIELD_KEYS.CE_THETA) || 0} decimals={2} /></td>}
            {isVisible('vega') && <td className={`${tdBase} ${callBg} text-right`}><NumberCell value={getFieldValue(row, FIELD_KEYS.CE_VEGA) || 0} decimals={2} /></td>}
            {isVisible('oi') && <td className={`${tdBase} ${callBg}`}>{renderDataCell(ceOI, maxValues.maxOI, formatOI(ceOI), 'red', 'right')}</td>}
            {isVisible('oi_change') && <td className={`${tdBase} ${callBg}`}>{renderDataCell(ceOIChg, maxValues.ceOIChange, formatOI(ceOIChg), ceOIChg > 0 ? 'green' : 'red', 'right')}</td>}
            {isVisible('volume') && <td className={`${tdBase} ${callBg}`}>{renderDataCell(ceVol, maxValues.maxVol, formatVolume(ceVol), 'red', 'right')}</td>}
            {isVisible('vol_oi') && <td className={`${tdBase} ${callBg}`}>{renderDataCell(ceVolOi, Math.max(maxValues.maxVol, maxValues.maxOI), formatVolume(Math.abs(ceVolOi)), ceVolOi >= 0 ? 'green' : 'red', 'right')}</td>}
            {isVisible('vol_oi_chng') && <td className={`${tdBase} ${callBg}`}>{renderDataCell(ceVolOiChng, Math.max(maxValues.maxVol, maxValues.maxOI), formatVolume(Math.abs(ceVolOiChng)), ceVolOiChng >= 0 ? 'green' : 'red', 'right')}</td>}
            {isVisible('iv') && <td className={`${tdBase} ${callBg}`}>{renderDataCell(ceIV || 0, maxValues.maxIV || 100, `${(ceIV || 0).toFixed(1)}`, 'red', 'right', true)}</td>}
            {isVisible('change_p') && <td className={`${tdBase} ${callBg}`}><LiveGlassCell symbol={symbol} strike={row.strike} side="ce" dataKey="changePercent" chgRefKey="changePercent" max={maxValues.ceChangeP} isPercent={true} align="right" showBar={true} initialVal={ceChgP} initialChgRef={ceChgP} /></td>}
            {isVisible('ltp') && <td className={`${tdBase} ${callBg}`}><LiveGlassCell symbol={symbol} strike={row.strike} side="ce" dataKey="ltp" chgRefKey="changePercent" max={0} isPercent={false} align="right" showBar={false} initialVal={ceLtp} initialChgRef={ceChgP} /></td>}
            {isVisible('ce_support_prem') && <td className={`${tdBase} ${callBg}`}><div className="flex justify-end items-center h-full"><span title={`Intraday & Macro Call Support Floor (Weighted by ${activeSubTab || 'Combined OI'})`} className="inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shadow-xs tabular-nums transition-all hover:scale-105 select-none">₹{ceSuppVal.toFixed(2)}</span></div></td>}
            {isVisible('ce_poc_prem') && <td className={`${tdBase} ${callBg}`}><div className="flex justify-end items-center h-full"><span title={`Intraday & Macro Call POC Level (${premiumProfile?.ceStrength || 'NORMAL'} - Weighted by ${activeSubTab || 'Combined OI'})`} className="inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/40 ring-1 ring-indigo-500/20 shadow-sm tabular-nums transition-all hover:scale-105 select-none"><span className="text-amber-500 animate-pulse">★</span>₹{premiumProfile?.cePoc ? premiumProfile.cePoc.toFixed(2) : ceLtp.toFixed(2)}</span></div></td>}
            {isVisible('ce_res_prem') && <td className={`${tdBase} ${callBg}`}><div className="flex justify-end items-center h-full"><span title={`Intraday & Macro Call Resistance Ceiling (Weighted by ${activeSubTab || 'Combined OI'})`} className="inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30 shadow-xs tabular-nums transition-all hover:scale-105 select-none">₹{ceResVal.toFixed(2)}</span></div></td>}
            {isVisible('combined_oi') && <td className={`${tdBase} ${callBg}`}>{renderDataCell(combinedOI, maxValues.maxCombinedOI, formatCombinedOI(combinedOI), 'red', 'right', true, oiBadge)}</td>}
            {isVisible('combined_oi_change') && <td className={`${tdBase} ${callBg}`}>{renderDataCell(combinedOIChange, maxValues.maxCombinedOIChange || 0, formatOI(combinedOIChange), combinedOIChange > 0 ? 'green' : 'red', 'right', true, oiChgBadge)}</td>}
            {isVisible('combined_oi_change_p') && <td className={`${tdBase} ${callBg}`}>{renderGlassCell(chgOIP, combinedOIChange, maxValues.maxCombinedOIChangeP || 100, true, 'right', true, oiChgPBadge)}</td>}
            {isVisible('vol_diff') && <td className={`${tdBase} ${isAtm ? 'bg-indigo-500/10' : 'bg-slate-50/20 dark:bg-slate-900/20'} text-center`}>{renderDataCell(volDiff, Math.max(Math.abs(top3VolDiff[0] || 0), Math.abs(bot3VolDiff[0] || 0)), formatVolume(Math.abs(volDiff)), volDiff > 0 ? 'green' : 'red', 'right', true, volDiffBadge)}</td>}
            {isVisible('oi_diff') && <td className={`${tdBase} ${isAtm ? 'bg-indigo-500/10' : 'bg-slate-50/20 dark:bg-slate-900/20'} text-center`}>{renderDataCell(oiDiff, Math.max(Math.abs(top3OIDiff[0] || 0), Math.abs(bot3OIDiff[0] || 0)), formatOI(Math.abs(oiDiff)), oiDiff > 0 ? 'green' : 'red', 'right', true, oiDiffBadge)}</td>}

            {/* STICKY STRIKE */}
            <td className={`p-0 align-middle sticky left-0 z-10 ${isAtm ? 'bg-transparent' : 'bg-white dark:bg-slate-900'} backdrop-blur-md text-center px-1 border-b border-transparent`}>
                <StrikeChip strike={row.strike} label={rowLabel?.text} type={isAtm ? 'atm' : rowLabel?.text ? undefined : 'neutral'} />
            </td>

            {/* PUTS SIDE */}
            {isVisible('combined_vol_change') && <td className={`${tdBase} ${putBg}`}>{combinedVolChange === null ? <div className="flex justify-start items-center h-full"><span className="text-slate-400 font-bold ml-4">-</span></div> : renderDataCell(combinedVolChange, maxValues.maxCombinedVolChange || 100, formatVolume(combinedVolChange), combinedVolChange > 0 ? 'green' : 'red', 'left', true, volChgBadge)}</td>}
            {isVisible('combined_vol_change_p') && <td className={`${tdBase} ${putBg}`}>{chgVolP === null ? <div className="flex justify-start items-center h-full"><span className="text-slate-400 font-bold ml-4">-</span></div> : renderGlassCell(chgVolP, combinedVolChange!, maxValues.maxCombinedVolChangeP || 100, true, 'left', true, volChgPBadge)}</td>}
            {isVisible('combined_volume') && <td className={`${tdBase} ${putBg}`}>{renderDataCell(combinedVol, maxValues.maxCombinedVol, formatCombinedVol(combinedVol), 'green', 'left', true, volBadge)}</td>}
            {isVisible('ltp') && <td className={`${tdBase} ${putBg}`}><LiveGlassCell symbol={symbol} strike={row.strike} side="pe" dataKey="ltp" chgRefKey="changePercent" max={0} isPercent={false} align="left" showBar={false} initialVal={peLtp} initialChgRef={peChgP} /></td>}
            {isVisible('pe_support_prem') && <td className={`${tdBase} ${putBg}`}>{/* PE Supp */}<div className="flex justify-start items-center h-full"><span title={`Intraday & Macro Put Support Floor (Weighted by ${activeSubTab || 'Combined OI'})`} className="inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shadow-xs tabular-nums transition-all hover:scale-105 select-none">₹{peSuppVal.toFixed(2)}</span></div></td>}
            {isVisible('pe_poc_prem') && <td className={`${tdBase} ${putBg}`}>{/* PE POC */}<div className="flex justify-start items-center h-full"><span title={`Intraday & Macro Put POC Level (${premiumProfile?.peStrength || 'NORMAL'} - Weighted by ${activeSubTab || 'Combined OI'})`} className="inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/40 ring-1 ring-indigo-500/20 shadow-sm tabular-nums transition-all hover:scale-105 select-none"><span className="text-amber-500 animate-pulse">★</span>₹{premiumProfile?.pePoc ? premiumProfile.pePoc.toFixed(2) : peLtp.toFixed(2)}</span></div></td>}
            {isVisible('pe_res_prem') && <td className={`${tdBase} ${putBg}`}>{/* PE Resist */}<div className="flex justify-start items-center h-full"><span title={`Intraday & Macro Put Resistance Ceiling (Weighted by ${activeSubTab || 'Combined OI'})`} className="inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30 shadow-xs tabular-nums transition-all hover:scale-105 select-none">₹{peResVal.toFixed(2)}</span></div></td>}
            {isVisible('change_p') && <td className={`${tdBase} ${putBg}`}><LiveGlassCell symbol={symbol} strike={row.strike} side="pe" dataKey="changePercent" chgRefKey="changePercent" max={maxValues.peChangeP} isPercent={true} align="left" showBar={true} initialVal={peChgP} initialChgRef={peChgP} /></td>}
            {isVisible('iv') && <td className={`${tdBase} ${putBg}`}>{renderDataCell(peIV || 0, maxValues.maxIV || 100, `${(peIV || 0).toFixed(1)}`, 'green', 'left', true)}</td>}
            {isVisible('vol_oi_chng') && <td className={`${tdBase} ${putBg}`}>{renderDataCell(peVolOiChng, Math.max(maxValues.maxVol, maxValues.maxOI), formatVolume(Math.abs(peVolOiChng)), peVolOiChng >= 0 ? 'green' : 'red', 'left')}</td>}
            {isVisible('vol_oi') && <td className={`${tdBase} ${putBg}`}>{renderDataCell(peVolOi, Math.max(maxValues.maxVol, maxValues.maxOI), formatVolume(Math.abs(peVolOi)), peVolOi >= 0 ? 'green' : 'red', 'left')}</td>}
            {isVisible('volume') && <td className={`${tdBase} ${putBg}`}>{renderDataCell(peVol, maxValues.maxVol, formatVolume(peVol), 'green', 'left')}</td>}
            {isVisible('oi_change') && <td className={`${tdBase} ${putBg}`}>{renderDataCell(peOIChg, maxValues.peOIChange, formatOI(peOIChg), peOIChg > 0 ? 'green' : 'red', 'left')}</td>}
            {isVisible('oi') && <td className={`${tdBase} ${putBg}`}>{renderDataCell(peOI, maxValues.maxOI, formatOI(peOI), 'green', 'left')}</td>}
            {isVisible('vega') && <td className={`${tdBase} ${putBg} text-left`}><NumberCell value={getFieldValue(row, FIELD_KEYS.PE_VEGA) || 0} decimals={2} /></td>}
            {isVisible('theta') && <td className={`${tdBase} ${putBg} text-left`}><NumberCell value={getFieldValue(row, FIELD_KEYS.PE_THETA) || 0} decimals={2} /></td>}
            {isVisible('gamma') && <td className={`${tdBase} ${putBg} text-left`}><NumberCell value={getFieldValue(row, FIELD_KEYS.PE_GAMMA) || 0} decimals={4} /></td>}
            {isVisible('delta') && <td className={`${tdBase} ${putBg} text-left`}><NumberCell value={getFieldValue(row, FIELD_KEYS.PE_DELTA) || 0} decimals={2} /></td>}
        </tr>
    )
})

// Helper for Cell Content moved outside to maintain StrikeRow React.memo integrity
const renderDataCell = (
    val: number,
    max: number,
    fmt: string,
    color: 'green' | 'red',
    align: 'left' | 'right',
    showBar = true,
    subText?: React.ReactNode,
) => {
    const pct = max > 0 ? (Math.abs(val) / max) * 100 : 0;
    return (
        <div className={`flex flex-col justify-center h-full ${align === 'right' ? 'items-end' : 'items-start'}`}>
            <div className="flex items-center gap-1 mb-1">
                {subText && align === 'right' && subText}
                <DataChip value={fmt} color={color} className={align === 'right' ? 'ml-auto' : 'mr-auto'} />
                {subText && align === 'left' && subText}
            </div>
            {showBar && (
                <div className="w-full max-w-[80px] mt-1">
                    <DataBar value={pct} color={color} align={align} showSpark={true} />
                </div>
            )}
        </div>
    )
}

const renderGlassCell = (
    val: number,
    chgRef: number,
    max: number,
    isPercent: boolean,
    align: 'left' | 'right',
    showBar: boolean,
    subText?: React.ReactNode
) => {
    const isPos = chgRef > 0;
    const isNeg = chgRef < 0;
    const color = chgRef > 0 ? 'green' : 'red';
    const glassClasses = isPos
        ? 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-emerald-300/60 dark:ring-emerald-700/40 text-emerald-700 dark:text-emerald-400 shadow-[0_2px_8px_rgba(16,185,129,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(16,185,129,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(16,185,129,0.15)] hover:-translate-y-px'
        : isNeg
            ? 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-rose-300/60 dark:ring-rose-700/40 text-rose-700 dark:text-rose-400 shadow-[0_2px_8px_rgba(244,63,94,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(244,63,94,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(244,63,94,0.15)] hover:-translate-y-px'
            : 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-slate-300/60 dark:ring-slate-700/40 text-slate-500 dark:text-slate-400 shadow-[0_2px_8px_rgba(148,163,184,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:-translate-y-px';

    const formattedVal = isPercent ? `${val.toFixed(1)}%` : val.toFixed(2);
    const pct = max > 0 ? (Math.abs(val) / max) * 100 : 0;

    return (
        <div className={`flex flex-col justify-center h-full ${align === 'right' ? 'items-end' : 'items-start'}`}>
            <div className={`flex items-center gap-1 mb-1 ${align === 'right' ? 'ml-auto' : 'mr-auto'}`}>
                {subText && align === 'right' && subText}
                <div className={`relative inline-flex w-fit min-w-[70px] px-2 h-[30px] items-center justify-center rounded-md text-[13px] font-black tabular-nums tracking-tight transition-all duration-300 ${glassClasses}`}>
                    {isPercent && <span className="text-[10px] mr-1 opacity-80">{isPos ? '▲' : isNeg ? '▼' : '—'}</span>}
                    {isPercent && isPos ? '+' : ''}{formattedVal}
                </div>
                {subText && align === 'left' && subText}
            </div>
            {showBar && (
                <div className="w-full max-w-[80px] mt-1">
                    <DataBar value={pct} color={color} align={align} showSpark={true} />
                </div>
            )}
        </div>
    )
}

const COLUMN_NAMES: Record<string, string> = {
    strike: 'Strike',
    ce_delta: 'CE Delta',
    ce_gamma: 'CE Gamma',
    ce_theta: 'CE Theta',
    ce_vega: 'CE Vega',
    ce_oi: 'CE OI',
    ce_oi_change: 'CE OI Chg',
    ce_volume: 'CE Volume',
    ce_vol_oi: 'CE Vol-OI',
    ce_vol_oi_chng: 'CE Vol-OI-Chng',
    ce_iv: 'CE IV',
    ce_change_p: 'CE Chg%',
    ce_ltp: 'CE LTP',
    ce_support_prem: 'CE Support',
    ce_poc_prem: 'CE POC',
    ce_res_prem: 'CE Resistance',
    combined_oi: 'Comb OI',
    combined_oi_change: 'Comb OI Chg',
    combined_oi_change_p: 'Comb OI Chg%',
    vol_diff: 'Vol Diff',
    oi_diff: 'OI Diff',
    combined_vol_change: 'Comb Vol Chg',
    combined_vol_change_p: 'Comb Vol Chg%',
    combined_volume: 'Comb Vol',
    pe_ltp: 'PE LTP',
    pe_support_prem: 'PE Support',
    pe_poc_prem: 'PE POC',
    pe_res_prem: 'PE Resistance',
    pe_change_p: 'PE Chg%',
    pe_iv: 'PE IV',
    pe_vol_oi_chng: 'PE Vol-OI-Chng',
    pe_vol_oi: 'PE Vol-OI',
    pe_volume: 'PE Volume',
    pe_oi_change: 'PE OI Chg',
    pe_oi: 'PE OI',
    pe_vega: 'PE Vega',
    pe_theta: 'PE Theta',
    pe_gamma: 'PE Gamma',
    pe_delta: 'PE Delta',
}

const getSortValue = (
    row: OptionData,
    key: string,
    profileMap: Map<number, any>,
    baseOptionsMap: Map<number, OptionData>
): number => {
    const ceLtp = Number(getFieldValue(row, FIELD_KEYS.CE_LTP)) || 0
    const ceOI = Number(getFieldValue(row, FIELD_KEYS.CE_OI)) || 0
    const ceOIChg = Number(getFieldValue(row, FIELD_KEYS.CE_OI_CHG)) || 0
    const ceVol = Number(getFieldValue(row, FIELD_KEYS.CE_VOL)) || 0
    const ceIV = Number(getFieldValue(row, FIELD_KEYS.CE_IV)) || 0
    const ceChgP = Number(getFieldValue(row, FIELD_KEYS.CE_PRICE_CHG)) || 0

    const peLtp = Number(getFieldValue(row, FIELD_KEYS.PE_LTP)) || 0
    const peOI = Number(getFieldValue(row, FIELD_KEYS.PE_OI)) || 0
    const peOIChg = Number(getFieldValue(row, FIELD_KEYS.PE_OI_CHG)) || 0
    const peVol = Number(getFieldValue(row, FIELD_KEYS.PE_VOL)) || 0
    const peIV = Number(getFieldValue(row, FIELD_KEYS.PE_IV)) || 0
    const peChgP = Number(getFieldValue(row, FIELD_KEYS.PE_PRICE_CHG)) || 0

    const profile = profileMap.get(Number(row.strike))

    switch (key) {
        // Strike
        case 'strike':
            return Number(row.strike) || 0

        // Calls side
        case 'ce_delta':
            return Number(getFieldValue(row, FIELD_KEYS.CE_DELTA)) || 0
        case 'ce_gamma':
            return Number(getFieldValue(row, FIELD_KEYS.CE_GAMMA)) || 0
        case 'ce_theta':
            return Number(getFieldValue(row, FIELD_KEYS.CE_THETA)) || 0
        case 'ce_vega':
            return Number(getFieldValue(row, FIELD_KEYS.CE_VEGA)) || 0
        case 'ce_oi':
            return ceOI
        case 'ce_oi_change':
            return ceOIChg
        case 'ce_volume':
            return ceVol
        case 'ce_vol_oi':
            return ceVol - ceOI
        case 'ce_vol_oi_chng': {
            const ceVolOi = ceVol - ceOI
            return ceOIChg > 0 ? ceVolOi - ceOIChg : ceVolOi + ceOIChg
        }
        case 'ce_iv':
            return ceIV
        case 'ce_change_p':
            return ceChgP
        case 'ce_ltp':
            return ceLtp
        case 'ce_support_prem':
            return profile?.ceSupport ?? ceLtp
        case 'ce_poc_prem':
            return profile?.cePoc ?? ceLtp
        case 'ce_res_prem':
            return profile?.ceRes ?? ceLtp
        case 'combined_oi':
            return ceOI + peOI
        case 'combined_oi_change':
            return ceOIChg + peOIChg
        case 'combined_oi_change_p': {
            const combinedOI = ceOI + peOI
            const combinedOIChange = ceOIChg + peOIChg
            return combinedOI > 0 ? (combinedOIChange / (combinedOI - combinedOIChange)) * 100 : 0
        }
        case 'vol_diff':
            return ceVol - peVol
        case 'oi_diff':
            return peOI - ceOI

        // Puts side
        case 'combined_vol_change': {
            const combinedVol = ceVol + peVol
            const bOpt = baseOptionsMap.get(Number(row.strike))
            const bCombinedVol = bOpt
                ? (Number(getFieldValue(bOpt, FIELD_KEYS.CE_VOL)) || 0) +
                  (Number(getFieldValue(bOpt, FIELD_KEYS.PE_VOL)) || 0)
                : 0
            return bOpt ? combinedVol - bCombinedVol : 0
        }
        case 'combined_vol_change_p': {
            const combinedVol = ceVol + peVol
            const bOpt = baseOptionsMap.get(Number(row.strike))
            const bCombinedVol = bOpt
                ? (Number(getFieldValue(bOpt, FIELD_KEYS.CE_VOL)) || 0) +
                  (Number(getFieldValue(bOpt, FIELD_KEYS.PE_VOL)) || 0)
                : 0
            const chg = bOpt ? combinedVol - bCombinedVol : 0
            return bCombinedVol !== 0 ? (chg / bCombinedVol) * 100 : 0
        }
        case 'combined_volume':
            return ceVol + peVol
        case 'pe_ltp':
            return peLtp
        case 'pe_support_prem':
            return profile?.peSupport ?? peLtp
        case 'pe_poc_prem':
            return profile?.pePoc ?? peLtp
        case 'pe_res_prem':
            return profile?.peRes ?? peLtp
        case 'pe_change_p':
            return peChgP
        case 'pe_iv':
            return peIV
        case 'pe_vol_oi_chng': {
            const peVolOi = peVol - peOI
            return peOIChg > 0 ? peVolOi - peOIChg : peVolOi + peOIChg
        }
        case 'pe_vol_oi':
            return peVol - peOI
        case 'pe_volume':
            return peVol
        case 'pe_oi_change':
            return peOIChg
        case 'pe_oi':
            return peOI
        case 'pe_vega':
            return Number(getFieldValue(row, FIELD_KEYS.PE_VEGA)) || 0
        case 'pe_theta':
            return Number(getFieldValue(row, FIELD_KEYS.PE_THETA)) || 0
        case 'pe_gamma':
            return Number(getFieldValue(row, FIELD_KEYS.PE_GAMMA)) || 0
        case 'pe_delta':
            return Number(getFieldValue(row, FIELD_KEYS.PE_DELTA)) || 0

        default:
            return Number(row.strike) || 0
    }
}

const OptionChainTableContent: React.FC<OptionChainTableProps> = React.memo(
    ( { marketData, analysis, filteredOptions, baseOptions, maxValues, isVisible, className, style } ) =>
    {
        const deferredOptions = filteredOptions

        const symbol = marketData?.symbol || ''
        const step = getStrikeStep(symbol, marketData?.options || [])
        const activeSubTab = useSettingsStore((s: any) => s.subTab || 'Combined OI')
        const strikeRange = useSettingsStore((s: any) => s.strikeRange || 11)
        const { profileMap } = useStrikePremiumProfile(
            symbol,
            step,
            marketData?.spotPrice,
            strikeRange,
            activeSubTab,
            '',
            filteredOptions
        )

        const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' }>({
            key: 'strike',
            direction: 'desc',
        })

        const handleSort = React.useCallback((key: string) => {
            setSortConfig((prev) => {
                if (prev.key === key) {
                    return { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
                }
                return { key, direction: 'desc' }
            })
        }, [])

        const handleResetSort = React.useCallback(() => {
            setSortConfig({ key: 'strike', direction: 'desc' })
        }, [])

        const baseOptionsMap = React.useMemo(() => {
            const map = new Map<number, OptionData>()
            if (baseOptions) {
                for (const b of baseOptions) {
                    map.set(Number(b.strike), b)
                }
            }
            return map
        }, [baseOptions])

        const sortedDeferredOptions = React.useMemo(() => {
            const optionsCopy = [...deferredOptions]
            if (sortConfig.key === 'strike') {
                return optionsCopy.sort((a, b) =>
                    sortConfig.direction === 'asc' ? a.strike - b.strike : b.strike - a.strike
                )
            }

            return optionsCopy.sort((a, b) => {
                const aVal = getSortValue(a, sortConfig.key, profileMap, baseOptionsMap)
                const bVal = getSortValue(b, sortConfig.key, profileMap, baseOptionsMap)

                if (aVal !== bVal) {
                    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
                }

                // Secondary tie-breaker: strike descending
                return b.strike - a.strike
            })
        }, [deferredOptions, sortConfig, profileMap, baseOptionsMap])

        const isCustomSorted = sortConfig.key !== 'strike' || sortConfig.direction !== 'desc'

        return (
            <div className="flex flex-col w-full h-full gap-2">
                {isCustomSorted && (
                    <div className="flex items-center justify-between px-4 py-2 bg-indigo-50/80 dark:bg-indigo-950/40 border border-indigo-200/70 dark:border-indigo-800/50 rounded-xl text-xs backdrop-blur-md animate-in fade-in slide-in-from-top-1 duration-200 shadow-xs">
                        <div className="flex items-center gap-2">
                            <span className="font-extrabold uppercase tracking-wider text-[10px] text-indigo-500 dark:text-indigo-400">
                                Sorted By:
                            </span>
                            <span className="font-black text-indigo-900 dark:text-indigo-100">
                                {COLUMN_NAMES[sortConfig.key] || sortConfig.key}
                            </span>
                            <span className="inline-flex items-center gap-1 font-semibold text-indigo-600 dark:text-indigo-300">
                                {sortConfig.direction === 'desc' ? (
                                    <>
                                        <ArrowDown size={12} className="stroke-[2.5]" /> Highest to Lowest
                                    </>
                                ) : (
                                    <>
                                        <ArrowUp size={12} className="stroke-[2.5]" /> Lowest to Highest
                                    </>
                                )}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={handleResetSort}
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-500 dark:hover:text-white border border-indigo-200 dark:border-indigo-700 shadow-xs transition-all cursor-pointer active:scale-95"
                            title="Reset to default sort (Strike: Highest to Lowest)"
                        >
                            <RotateCcw size={12} />
                            <span>Reset Sort</span>
                        </button>
                    </div>
                )}

                <TableWrapper className={ `relative w-full max-w-full overflow-hidden ${className || ''}` } style={ style }>
                    <div className="w-full overflow-x-auto custom-scrollbar">
                        <table className="w-full border-separate border-spacing-y-[2px]">
                            <OptionChainHeader
                                isVisible={ isVisible }
                                sortKey={ sortConfig.key }
                                sortDirection={ sortConfig.direction }
                                onSort={ handleSort }
                                onResetSort={ handleResetSort }
                            />
                        <tbody className="divide-y divide-slate-100/0">
                            { ( () =>
                            {
                                if (deferredOptions.length === 0) {
                                    return (
                                        <tr>
                                            <td colSpan={24} className="py-12 text-center italic text-slate-400">
                                                Waiting for market data...
                                            </td>
                                        </tr>
                                    )
                                }

                                // 1. Calculate Top 3 thresholds for Combined OI and Combined Volume
                                const sortedCombinedOI = [ ...filteredOptions ]
                                    .map(
                                        ( r ) =>
                                            ( getFieldValue( r, FIELD_KEYS.CE_OI ) || 0 ) +
                                            ( getFieldValue( r, FIELD_KEYS.PE_OI ) || 0 ),
                                    )
                                    .sort( ( a, b ) => b - a )
                                const top3OI = Array.from( new Set( sortedCombinedOI ) ).slice( 0, 3 )

                                const sortedCombinedVol = [ ...filteredOptions ]
                                    .map(
                                        ( r ) =>
                                            ( getFieldValue( r, FIELD_KEYS.CE_VOL ) || 0 ) +
                                            ( getFieldValue( r, FIELD_KEYS.PE_VOL ) || 0 ),
                                    )
                                    .sort( ( a, b ) => b - a )
                                const top3Vol = Array.from( new Set( sortedCombinedVol ) ).slice( 0, 3 )

                                const allChgOI: number[] = []
                                const allChgOIP: number[] = []
                                const allChgVol: number[] = []
                                const allChgVolP: number[] = []
                                const allVolDiff: number[] = []
                                const allOIDiff: number[] = []
                                
                                for (const r of filteredOptions) {
                                    const ceOI = getFieldValue(r, FIELD_KEYS.CE_OI) || 0
                                    const peOI = getFieldValue(r, FIELD_KEYS.PE_OI) || 0
                                    const ceVol = getFieldValue(r, FIELD_KEYS.CE_VOL) || 0
                                    const peVol = getFieldValue(r, FIELD_KEYS.PE_VOL) || 0
                                    const ceOIChg = getFieldValue(r, FIELD_KEYS.CE_OI_CHG) || 0
                                    const peOIChg = getFieldValue(r, FIELD_KEYS.PE_OI_CHG) || 0
                                    
                                    const combinedOI = ceOI + peOI
                                    const combinedVol = ceVol + peVol
                                    
                                    const bOpt = baseOptions?.find( ( b ) => Number(b.strike) === Number(r.strike) )
                                    const bCombinedOI = bOpt ? ( getFieldValue(bOpt, FIELD_KEYS.CE_OI) || 0 ) + ( getFieldValue(bOpt, FIELD_KEYS.PE_OI) || 0 ) : 0
                                    const bCombinedVol = bOpt ? ( getFieldValue(bOpt, FIELD_KEYS.CE_VOL) || 0 ) + ( getFieldValue(bOpt, FIELD_KEYS.PE_VOL) || 0 ) : 0
                                    
                                    const hasBase = !!bOpt
                                    
                                    const chgOI = ceOIChg + peOIChg
                                    const chgOIP = combinedOI > 0 ? (chgOI / (combinedOI - chgOI)) * 100 : 0
                                    
                                    if (chgOI !== 0) allChgOI.push(chgOI)
                                    if (chgOIP !== 0) allChgOIP.push(chgOIP)
                                    
                                    if (hasBase) {
                                        const chgVol = combinedVol - bCombinedVol
                                        const chgVolP = bCombinedVol !== 0 ? (chgVol / bCombinedVol) * 100 : (chgVol > 0 ? 100 : 0)
                                        if (chgVol !== 0) allChgVol.push(chgVol)
                                        if (chgVolP !== 0) allChgVolP.push(chgVolP)
                                    }

                                    allVolDiff.push(ceVol - peVol)
                                    allOIDiff.push(peOI - ceOI)
                                }

                                const uniqueChgOI = Array.from(new Set(allChgOI)).sort((a, b) => b - a)
                                const top3ChgOI = uniqueChgOI.slice(0, 3)
                                const bot3ChgOI = uniqueChgOI.slice(-3).reverse()

                                const uniqueChgOIP = Array.from(new Set(allChgOIP)).sort((a, b) => b - a)
                                const top3ChgOIP = uniqueChgOIP.slice(0, 3)
                                const bot3ChgOIP = uniqueChgOIP.slice(-3).reverse()

                                const uniqueChgVol = Array.from(new Set(allChgVol)).sort((a, b) => b - a)
                                const top3ChgVol = uniqueChgVol.slice(0, 3)
                                const bot3ChgVol = uniqueChgVol.slice(-3).reverse()

                                const uniqueChgVolP = Array.from(new Set(allChgVolP)).sort((a, b) => b - a)
                                const top3ChgVolP = uniqueChgVolP.slice(0, 3)
                                const bot3ChgVolP = uniqueChgVolP.slice(-3).reverse()

                                const uniqueVolDiff = Array.from(new Set(allVolDiff)).sort((a, b) => b - a)
                                const top3VolDiff = uniqueVolDiff.slice(0, 3)
                                const bot3VolDiff = uniqueVolDiff.slice(-3).reverse()

                                const uniqueOIDiff = Array.from(new Set(allOIDiff)).sort((a, b) => b - a)
                                const top3OIDiff = uniqueOIDiff.slice(0, 3)
                                const bot3OIDiff = uniqueOIDiff.slice(-3).reverse()

                                return sortedDeferredOptions.map( ( row, idx ) => {
                                    const options = marketData?.options || []
                                    const step = getStrikeStep( marketData?.symbol || '', options )
                                    const isAtm = marketData ? Math.abs( row.strike - marketData.spotPrice ) < step / 2 : false
                                    const atmClass = isAtm ? 'animate-breathe-atm z-[40] relative border-y border-yellow-200/50 dark:border-yellow-600/30' : ''
                                    const hoverClass = 'hover:scale-[1.005] hover:shadow-xl hover:z-20 hover:bg-white dark:hover:bg-slate-800 transition-all duration-200 hover:border-transparent cursor-default'

                                    return (
                                        <StrikeRow 
                                            key={row.strike}
                                            row={row}
                                            rowIndex={idx}
                                            isAtm={isAtm}
                                            atmClass={atmClass}
                                            hoverClass={hoverClass}
                                            isVisible={isVisible}
                                            maxValues={maxValues}
                                            renderDataCell={renderDataCell}
                                            renderGlassCell={renderGlassCell}
                                            top3OI={top3OI}
                                            bot3OI={[]}
                                            top3OIChg={top3ChgOI}
                                            bot3OIChg={bot3ChgOI}
                                            top3ChgOIP={top3ChgOIP}
                                            bot3ChgOIP={bot3ChgOIP}
                                            top3Vol={top3Vol}
                                            bot3Vol={[]}
                                            top3ChgVol={top3ChgVol}
                                            bot3ChgVol={bot3ChgVol}
                                            top3ChgVolP={top3ChgVolP}
                                            bot3ChgVolP={bot3ChgVolP}
                                            analysis={analysis}
                                            top3VolDiff={top3VolDiff}
                                            bot3VolDiff={bot3VolDiff}
                                            top3OIDiff={top3OIDiff}
                                            bot3OIDiff={bot3OIDiff}
                                            baseOptions={baseOptions}
                                            symbol={marketData?.symbol || ''}
                                            premiumProfile={profileMap.get(Number(row.strike))}
                                            activeSubTab={activeSubTab}
                                        />
                                    )
                                })
                            } )() }
                        </tbody>
                    </table>
                </div>
            </TableWrapper>
            </div>
        )
    },
)

// 1. Export the Wrapper Component that contains the Error Boundary
export const OptionChainTable: React.FC<OptionChainTableProps> = ( props ) => (
    <WidgetErrorBoundary fallbackDescription="Option Chain">
        <OptionChainTableContent { ...props } />
    </WidgetErrorBoundary>
)
